import React, { Fragment, useEffect, useState } from "react";
import BootstrapSwitchButton from "bootstrap-switch-button-react";
import {
  Badge,
  Button,
  Breadcrumb,
  BreadcrumbItem,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";
import { cloneDeep, pick } from "lodash";
import { toastr } from "react-redux-toastr";

const DayOfWeekBadge = ({ dayName, isActive, onClick }) => {
  return (
    <Badge
      color={isActive ? "success" : "danger"}
      className="ml-2 mr-2"
      bsSize={24}
      onClick={onClick}
      style={{ cursor: "pointer" }}
    >
      {dayName}
    </Badge>
  );
};

const defaultDaySchedule = {
  monday_si: 0,
  tuesday_si: 0,
  wednesday_si: 0,
  thursday_si: 0,
  friday_si: 0,
  saturday_si: 0,
  sunday_si: 0,
};

export const CallWindowConfigForm = ({
  onSave,
  selectedCampaign,
  showLoader,
  isInternalUser,
  hasUpdatePermission,
}) => {
  const [templateId_si] = useState(selectedCampaign.id); //Root has id acting templateId. creditLineConfig maintains this id as templateId_si.
  const [callWindow, setCallWindow] = useState([]);
  const [daySchedule, setDaySchedule] = useState({ ...defaultDaySchedule });
  const [transferAgentActive_si, setTransferAgentActive_si] = useState(0);
  const [noCallWindow] = useState(
    selectedCampaign &&
      selectedCampaign.windowConfigList &&
      selectedCampaign.windowConfigList.length === 0
  );

  const extractValues = () => {
    setCallWindow([...cloneDeep(selectedCampaign.windowConfigList)]);
    setTransferAgentActive_si(extractTransferAgentActive(selectedCampaign));
    setDaySchedule(extractDaySchedule(selectedCampaign));
  };

  useEffect(() => {
    //DO NOT REMOVE BELOW COMMENT LINES Else build pipeline fails.
    extractValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    extractValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampaign, isInternalUser]);

  const onSaveClick = (ev) => {
    ev.stopPropagation();
    ev.preventDefault();
    let hasError = false;
    const notifyObject = cloneDeep(selectedCampaign);
    let newWindowConfigList = callWindow.map((item) => ({
      ...item,
      maxCalls_si: +item.maxCalls_si,
      transferAgentActive_si,
      templateId_si,
      ...daySchedule,
    }));

    newWindowConfigList.forEach((item) => {
      if (item.maxCalls_si < 0 || item.maxCalls_si > 32767) {
        toastr.error("Max Calls permitted values are between 0-32767");
        hasError = true;
      }
    });

    notifyObject.windowConfigList = newWindowConfigList;
    if (!hasError) {
      onSave(notifyObject);
    }
  };

  const extractTransferAgentActive = (passedState) => {
    try {
      return passedState.windowConfigList[0].transferAgentActive_si ? 1 : 0;
    } catch (ex) {
      return 0;
    }
  };

  const extractDaySchedule = (passedState) => {
    try {
      return pick(passedState.windowConfigList[0], Object.keys(defaultDaySchedule));
    } catch (ex) {
      return defaultDaySchedule;
    }
  };

  const updateDaySchedule = (field) => {
    setDaySchedule((currSchedule) => ({
      ...currSchedule,
      [field]: currSchedule[field] === 1 ? 0 : 1,
    }));
  };

  const FormButtonPanel = () => {
    return (
      selectedCampaign.windowConfigList && (
        <Fragment>
          <Button
            color="primary"
            onClick={(ev) => onSaveClick(ev)}
            disabled={showLoader}
            className="ml-5"
          >
            Save
          </Button>
          {"      "}
          <Button
            color="primary"
            onClick={() => extractValues()}
            disabled={showLoader}
            className="ml-2"
          >
            Cancel
          </Button>
        </Fragment>
      )
    );
  };

  const DrawBar = () => (
    <Row form>
      <Col md={12}>
        <hr />
      </Col>
    </Row>
  );

  const TimeLine = ({ startHour, endHour }) => {
    try {
      let stringTime = "";
      [startHour, endHour].forEach((item) => {
        if (stringTime) stringTime += " - ";
        if (item === 0 || item === 24) stringTime += "12:00 AM";
        if (item === 12) stringTime += "12:00 PM";
        if (item > 0 && item < 12) stringTime += `${item > 9 ? item : "0" + item}:00 AM`;
        if (item > 12 && item < 22) stringTime += `0${item - 12}:00 PM`;
        if (item > 21 && item < 24) stringTime += `${item - 12}:00 PM`;
      });
      return stringTime;
    } catch (ex) {
      return `${startHour} - ${endHour}`;
    }
  };

  const updateCallWindow = (index, prop, value) => {
    callWindow[index] = { ...callWindow[index], [prop]: value };
    setCallWindow([...callWindow]);
  };

  const NoCallWindowMessage = () => {
    return (
      noCallWindow && (
        <Row className="mb-2 mb-xl-4 align-center">
          <Col xs="auto">
            <Breadcrumb>
              <BreadcrumbItem>
                Sorry, Missing window calls set-up for selected template!
              </BreadcrumbItem>
            </Breadcrumb>
          </Col>
        </Row>
      )
    );
  };

  return (
    <Card key="CallWindowConfigForm_Card">
      <CardHeader>
        <CardTitle tag="h5" className="mb-0">
          Call Set-up
        </CardTitle>
      </CardHeader>
      <CardBody key="CallWindowConfigForm_CardBody">
        {NoCallWindowMessage()}
        {!noCallWindow && (
          <Form>
            <Row>
              <Col md="12">
                <FormGroup>
                  {selectedCampaign.company_code === "Illinois" && (
                    <Fragment>
                      <Label for="agentTransfer" className="mr-2">
                        Agent Transfer
                      </Label>
                      <BootstrapSwitchButton
                        checked={transferAgentActive_si === 1 ? true : false}
                        onlabel="On"
                        offlabel="Off"
                        onstyle="success"
                        offstyle="danger"
                        className="ml-2"
                        name="transferAgentActive_si"
                        onChange={(checked) => {
                          setTransferAgentActive_si(checked ? 1 : 0);
                        }}
                      />
                    </Fragment>
                  )}
                  {isInternalUser && (
                    <Fragment>
                      <Label for="agentTransfer" className="mr-2 ml-4">
                        Schedule
                      </Label>
                      {["monday_si", "tuesday_si", "wednesday_si", "thursday_si", "friday_si"].map(
                        (field) => (
                          <DayOfWeekBadge
                            key={field}
                            dayName={field.charAt(0).toUpperCase()}
                            isActive={daySchedule[field]}
                            onClick={() => updateDaySchedule(field)}
                          />
                        )
                      )}
                    </Fragment>
                  )}
                  {!isInternalUser && hasUpdatePermission && <FormButtonPanel />}
                </FormGroup>
              </Col>
            </Row>
            {isInternalUser && (
              <Fragment>
                <DrawBar />
                <Row key="call_window_head" form>
                  <Col md={6}>
                    <strong>Call Windows (CT)</strong>
                  </Col>
                  <Col md={6}>
                    <strong>Max Calls Per Window</strong>
                  </Col>
                </Row>
                <DrawBar />
                {callWindow.map((windowItem, index) => (
                  <Row key={`key_${index}`} form>
                    <Col md={6}>
                      <FormGroup>
                        <Label className="mr-5">
                          <TimeLine
                            startHour={windowItem.startHour_si}
                            endHour={windowItem.endHour_si}
                          />
                        </Label>
                        <BootstrapSwitchButton
                          checked={windowItem.isActive_si ? true : false}
                          onlabel="On"
                          name={`windowsId_${index}`}
                          key={`windowsId_vch_${index}`}
                          offlabel="Off"
                          onstyle="success"
                          offstyle="danger"
                          onChange={(checked) =>
                            updateCallWindow(index, "isActive_si", checked ? 1 : 0)
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <Input
                        type="number"
                        step="1"
                        name={`Window_MaxCalls_${index}`}
                        key={`Window_MaxCalls_${index}`}
                        id={`Window_MaxCalls_${index}`}
                        placeholder="0"
                        className="mr-2"
                        value={windowItem.maxCalls_si}
                        bsSize="20px"
                        maxLength="4"
                        min={0}
                        disabled={windowItem.isActive_si ? false : true}
                        max={1000}
                        onKeyDown={(ev) => {
                          if (ev.key === ".") {
                            ev.preventDefault();
                          }
                        }}
                        onChange={(ev) => {
                          updateCallWindow(
                            index,
                            "maxCalls_si",
                            Math.abs(
                              isNaN(ev.currentTarget.value)
                                ? 0
                                : Number(ev.currentTarget.value.replace(/[^0-9]*/g, ""))
                            )
                          );
                        }}
                      />
                    </Col>
                  </Row>
                ))}
                <DrawBar />
                {hasUpdatePermission && (
                  <Row>
                    <Col md={12} className="text-center">
                      <FormButtonPanel />
                    </Col>
                  </Row>
                )}
              </Fragment>
            )}
          </Form>
        )}
      </CardBody>
    </Card>
  );
};
